import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable, Subject, fromEvent } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import {
  CtrlNavigationContext,
  CtrlNavigationEntry,
  FeatureToggles,
  LastPath,
  NavigationPermissions,
} from '../../shared/_models/models';
import { trimStart } from 'lodash';
import {
  EXTERNAL_PARTNER_NAV_CONTEXT_LIST,
  EXTERNAL_PUBLISHER_NAV_CONTEXT_LIST,
  INTERNAL_NAV_CONTEXT_LIST,
  CtrlNavigationContextConfig,
  ctrlNavConfigs,
  spearadNavConfigs,
  internalNavConfigs,
  exchangeNavConfigs,
  partnerNavConfigs,
} from './unruly-navigation.config';
import { find } from 'lodash';
import { SpearadAppService } from '../../shared/_services/spearad-app.service';
import { AuthenticationService } from '../../auth/_services/authentication.service';
import { SharedStoreService } from '../../shared/_services/shared-store.service';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {

  readonly currentContext$: Observable<CtrlNavigationContext>;
  readonly lastPath$: Observable<LastPath>;
  readonly isCollapsed$: Observable<boolean>;
  readonly isGlobalSearchOpen$: Observable<boolean>;
  readonly isUpdateAvailable$: Observable<boolean>;
  readonly featureToggle$: Observable<FeatureToggles>;
  readonly isFeatureToggleLoading$: Observable<boolean>;
  readonly navigationPermissions$: Observable<NavigationPermissions>;
  readonly filteredNavContextList$: Observable<CtrlNavigationContextConfig[]>;
  readonly navConfigMap$: Observable<{ [key in CtrlNavigationContext]: CtrlNavigationEntry[] }>;

  private readonly currentContextSubject = new BehaviorSubject<CtrlNavigationContext>(null);
  private readonly lastPathSubject = new BehaviorSubject<LastPath>({
    ctrl: null,
    spearad: null,
    supply: null,
    exchange: null,
  });
  private readonly isCollapsedSubject = new BehaviorSubject<boolean>(false);
  private readonly isGlobalSearchOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly isUpdateAvailableSubject = new BehaviorSubject<boolean>(false);
  private readonly featureToggleSubject = new BehaviorSubject<FeatureToggles>({});
  private readonly isFeatureToggleLoadingSubject = new BehaviorSubject<boolean>(true);
  private readonly navigationPermissionsDefault: NavigationPermissions = {
    publisherIntegrationDetails: false,
    publisherDeal: false,
    publisherReporting: false,
    publisherReportingDeals: false,
    publisherReportingDetailed: false,
    publisherReportingDemand: false,
  };
  private readonly navigationPermissionsSubject = new BehaviorSubject<NavigationPermissions>(this.navigationPermissionsDefault);
  private readonly destroy$ = new Subject<void>();

  private navConfigMapSubject = new BehaviorSubject<{ [key in CtrlNavigationContext]: CtrlNavigationEntry[] }>({
    ctrl: ctrlNavConfigs,
    spearad: spearadNavConfigs,
    supply: internalNavConfigs,
    exchange: exchangeNavConfigs,
    partner: partnerNavConfigs
  });

  private filteredNavContextListSubject = new BehaviorSubject<CtrlNavigationContextConfig[]>([]);

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private spearadService: SpearadAppService,
    private authService: AuthenticationService,
    private sharedStoreService: SharedStoreService
  ) {
    this.currentContext$ = this.currentContextSubject.asObservable();
    this.lastPath$ = this.lastPathSubject.asObservable();
    this.isCollapsed$ = this.isCollapsedSubject.asObservable();
    this.isGlobalSearchOpen$ = this.isGlobalSearchOpenSubject.asObservable();
    this.isUpdateAvailable$ = this.isUpdateAvailableSubject.asObservable();
    this.featureToggle$ = this.featureToggleSubject.asObservable();
    this.isFeatureToggleLoading$ = this.isFeatureToggleLoadingSubject.asObservable();
    this.navigationPermissions$ = this.navigationPermissionsSubject.asObservable();
    this.filteredNavContextList$ = this.filteredNavContextListSubject.asObservable();
    this.navConfigMap$ = this.navConfigMapSubject.asObservable();
  }

  get currentContext(): CtrlNavigationContext {
    return this.currentContextSubject.value;
  }

  get filteredNavContextList(): CtrlNavigationContextConfig[] {
    return this.filteredNavContextListSubject.value;
  }

  get currentContextNavItems(): CtrlNavigationEntry[] {
    return this.filteredNavContextList.find(context => context.key === this.currentContext)?.navItems || [];
  }

  get isFeatureToggleLoading(): boolean {
    return this.isFeatureToggleLoadingSubject.value;
  }

  get navConfigMap(): { [key in CtrlNavigationContext]: CtrlNavigationEntry[] } {
    return this.navConfigMapSubject.value;
  }

  init(): void {
    this.initializeFromLocalStorage();
    this.setupWindowResizeListener();
    this.setupRouterEvents();
    this.setupFeatureToggleSubscription();
  }

  getLastPath(): LastPath {
    return this.lastPathSubject.value;
  }

  getContextTitle(): string {
    const currentContext = this.currentContext;
    const navContextList = this.filteredNavContextList;
    return find(navContextList, ['key', currentContext])?.title || '';
  }

  toggleCollapsed(): void {
    this.isCollapsedSubject.next(!this.isCollapsedSubject.value);
  }

  toggleGlobalSearch(): void {
    this.isGlobalSearchOpenSubject.next(!this.isGlobalSearchOpenSubject.value);
  }

  setUpdateAvailable(isAvailable: boolean): void {
    this.isUpdateAvailableSubject.next(isAvailable);
  }

  updateStateCurrentContext(context: CtrlNavigationContext, navigateToLastPath = false): void {
    if (this.currentContextSubject.value !== context) {
      this.currentContextSubject.next(context);
      if (navigateToLastPath && context) {
        this.navigateToLastPath();
      }
    }
  }

  updateStateLastPath(context: string, url: string): void {
    if (!context) return;
    const currentLastPath = this.lastPathSubject.value;
    const updatedLastPath = {
      ...currentLastPath,
      [context]: url || null,
    };
    this.lastPathSubject.next(updatedLastPath);
    localStorage.setItem('lastPath', JSON.stringify(updatedLastPath));
  }

  removeLastPath(): void {
    this.lastPathSubject.next({
      ctrl: null,
      spearad: null,
      supply: null,
      exchange: null,
    });
    localStorage.removeItem('lastPath');
  }

  updateNavigationPermissions(permissions: NavigationPermissions): void {
    this.navigationPermissionsSubject.next(permissions);
  }

  hasNavigationPermission(permission: keyof NavigationPermissions): boolean {
    return this.navigationPermissionsSubject.value[permission];
  }

  navigateToLastPath() {
    const navigatingToContext = this.currentContext;
    const lastPath = this.lastPathSubject.value;
    if (lastPath && lastPath[navigatingToContext]) {
      if (navigatingToContext === 'spearad') {
        this.spearadService.navigate(trimStart(lastPath.spearad.replace('/spearad', ''), '/'));
      }
      const url = new URL(`${location.origin}${lastPath[navigatingToContext]}`);
      const path = url.pathname.split('/').filter(segment => segment);
      const queryParams: Record<string, string> = {};
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      this.router.navigate(path,  { queryParams });
    } else {
      // Use homePage from context config via getNavContextList
      const navContextList = this.filteredNavContextList;
      const contextConfig = navContextList.find(c => c.key === navigatingToContext);
      let navigateToPath: string[];
      if (contextConfig?.homePageId) {
        const homePage = contextConfig.navItems
          .flatMap(item => item.children ?? [item])
          .find(item => item.id === contextConfig.homePageId);
        navigateToPath = homePage?.path;
      } else if (contextConfig?.navItems?.length) {
        const firstRoute: CtrlNavigationEntry = contextConfig.navItems[0];
        navigateToPath = firstRoute?.path;
      }
      if (navigateToPath) {
        if (navigatingToContext === 'spearad') {
          this.router.navigate(['spearad', ...navigateToPath]);
        } else {
          this.router.navigate(navigateToPath);
        }
      }
    }
  }

  isNavItemVisible(navItem: CtrlNavigationEntry) {
    let rendererPass = true;
    let permissionPass = true;
    let authConfigPass = true;
    let featureTogglePass = true;
    if (navItem.navigationPermissions) {
      permissionPass = navItem.navigationPermissions.every(permission => this.hasNavigationPermission(permission));
    }
    if (navItem.rendererFn) {
      rendererPass = navItem.rendererFn(navItem, this.currentContext);
    }
    if (navItem.rendererAuthConfig) {
      authConfigPass = this.authService.isPassAllNavigationEntryAuthConfigCriteria(navItem.rendererAuthConfig);
    }
    if (navItem.featureToggleKey) {
      featureTogglePass = this.featureToggleSubject.value[navItem.featureToggleKey];
    }
    return permissionPass && rendererPass && authConfigPass && featureTogglePass;
  }

  setNavConfigForContext(context: CtrlNavigationContext, entries: CtrlNavigationEntry[]): void {
    const current = { ...this.navConfigMapSubject.value };
    current[context] = entries;
    this.navConfigMapSubject.next(current);
  }

  updateFilteredNavContextList(): void {
    const navConfigMap = this.navConfigMapSubject.value;
    const contextListByUserType = [...this.getContextSwitchItemsByUserType()];
    const filtered = contextListByUserType
      .map(contextConfig => {
        const navEntries = navConfigMap[contextConfig.key] || [];
        const visibleNavItems = this.filterVisibleNavItems(navEntries);
        const navItemsWithCurrentSelected = this.markCurrentSelectedNavItem(visibleNavItems);
        return { ...contextConfig, navItems: navItemsWithCurrentSelected };
      })
      .filter(contextConfig => this.isContextConfigVisible(contextConfig));
    this.filteredNavContextListSubject.next(filtered);
  }

  isContextConfigVisible(contextConfig: CtrlNavigationContextConfig): boolean {
    return (contextConfig.allowEmpty || contextConfig.navItems?.length) &&
      (contextConfig.authConfig ? this.authService.isPassAllNavigationEntryAuthConfigCriteria(contextConfig.authConfig) : true);
  }

  updateCurrentNavItemsSelectedValue(): void {
    const filteredNavContextList = this.filteredNavContextList;
    const index = filteredNavContextList.findIndex(context => context.key === this.currentContext);
    const navItems = filteredNavContextList[index].navItems;
    const navItemsWithCurrentSelected = this.markCurrentSelectedNavItem(navItems);
    const updated = [...filteredNavContextList];
    updated[index].navItems = navItemsWithCurrentSelected;
    this.filteredNavContextListSubject.next(updated);
  }

  calculateFixedElementPositionSync(
    anchorElement: HTMLElement,
    fixedElement: HTMLElement,
    side: 'top' | 'right' | 'bottom' | 'left',
    marginPx: number = 0
  ): Partial<CSSStyleDeclaration> {
    if (!anchorElement || !fixedElement) {
      return {};
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    const fixedRect = fixedElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const style: Partial<CSSStyleDeclaration> = { position: 'fixed' };

    switch (side) {
      case 'right':
        style.top = `${anchorRect.top}px`;
        style.left = `${anchorRect.right + marginPx}px`;
        if (anchorRect.top + fixedRect.height > viewportHeight - marginPx) {
          style.top = null;
          style.bottom = `${marginPx}px`;
        }
        if (anchorRect.right + marginPx + fixedRect.width > viewportWidth - marginPx) {
          style.left = `${anchorRect.left - fixedRect.width - marginPx}px`;
        }
        break;

      case 'left':
        style.top = `${anchorRect.top}px`;
        style.left = `${anchorRect.left - fixedRect.width - marginPx}px`;
        if (anchorRect.top + fixedRect.height > viewportHeight - marginPx) {
          style.top = null;
          style.bottom = `${marginPx}px`;
        }
        if (anchorRect.left - fixedRect.width - marginPx < marginPx) {
          style.left = `${anchorRect.right + marginPx}px`;
        }
        break;

      case 'bottom':
        style.top = `${anchorRect.bottom + marginPx}px`;
        style.left = `${anchorRect.left}px`;
        if (anchorRect.left + fixedRect.width > viewportWidth - marginPx) {
          style.left = null;
          style.right = `${viewportWidth - anchorRect.right}px`;
        }
        if (anchorRect.bottom + marginPx + fixedRect.height > viewportHeight - marginPx) {
          style.top = null;
          style.bottom = `${viewportHeight - anchorRect.top + marginPx}px`;
        }
        break;

      case 'top':
        style.bottom = `${viewportHeight - anchorRect.top + marginPx}px`;
        style.left = `${anchorRect.left}px`;
        if (anchorRect.left + fixedRect.width > viewportWidth - marginPx) {
          style.left = null;
          style.right = `${viewportWidth - anchorRect.right}px`;
        }
        if (viewportHeight - anchorRect.top + marginPx + fixedRect.height > viewportHeight - marginPx) {
          style.bottom = null;
          style.top = `${anchorRect.bottom + marginPx}px`;
        }
        break;
    }
    return style;
  }

  filterVisibleNavItems(entries: CtrlNavigationEntry[]): CtrlNavigationEntry[] {
    if (!entries || !entries.length) return [];
    return entries
      .map(entry => {
        const hasChildren = entry.children && entry.children.length;
        const parentVisible = this.isNavItemVisible(entry);
        if (!parentVisible) {
          return null;
        }
        if (hasChildren) {
          const visibleChildren = this.filterVisibleNavItems(entry.children);
          if (visibleChildren.length) {
            return { ...entry, children: visibleChildren };
          }
          return null;
        }
        return entry;
      })
      .filter(Boolean);
  }

  destroy(): void {
    this.removeLastPath();
    this.isGlobalSearchOpenSubject.next(false);
    this.isCollapsedSubject.next(false);
    this.filteredNavContextListSubject.next([]);
    this.navigationPermissionsSubject.next(this.navigationPermissionsDefault);
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetSpearadNavConfig(): void {
    this.navConfigMapSubject.next({
      ...this.navConfigMapSubject.value,
      spearad: spearadNavConfigs,
    });
  }

  private initializeFromLocalStorage(): void {
    const storedLastPath = localStorage.getItem('lastPath');
    if (storedLastPath) {
      this.lastPathSubject.next(JSON.parse(storedLastPath));
    }
  }

  private setupWindowResizeListener(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(300)
        )
        .subscribe(() => {
          this.checkMobileBreakpoint();
        });
    });
    this.checkMobileBreakpoint();
  }

  private checkMobileBreakpoint(): void {
    const isMobile = window.innerWidth < 800;
    if (isMobile && !this.isCollapsedSubject.value) {
      this.isCollapsedSubject.next(true);
    }
  }

  private setupRouterEvents(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd),
        tap((navEvent: NavigationEnd) => {
          this.handleNavigationEnd(navEvent);
        }),
      )
      .subscribe();
  }

  private handleNavigationEnd(navEvent: NavigationEnd): void {
    const urlContextDict = {
      portal: 'ctrl',
      spearad: 'spearad',
      supply: 'supply',
      partner: 'partner',
      exchange: 'exchange',
    };
    const currContextFromUrl = urlContextDict[trimStart(navEvent.urlAfterRedirects, '/').split('/')[0]];
    if (currContextFromUrl) {
      if (currContextFromUrl !== this.currentContext || this.filteredNavContextList.length === 0) {
        this.updateStateCurrentContext(currContextFromUrl);
      } else {
        this.updateCurrentNavItemsSelectedValue();
      }
      this.updateStateLastPath(currContextFromUrl, navEvent.url);
    }
  }

  private setupFeatureToggleSubscription(): void {
    this.sharedStoreService.getSharedAsset('featureToggles').pipe(takeUntil(this.destroy$)).subscribe((featureToggles: FeatureToggles) => {
      this.featureToggleSubject.next(featureToggles);
      if (Object.values(featureToggles).some(toggleValue => toggleValue !== null)) {
        this.isFeatureToggleLoadingSubject.next(false);
      }
    });
  }

  private getContextSwitchItemsByUserType(): CtrlNavigationContextConfig[] {
    const currentUser = this.authService.currentUserValue;
    if (currentUser?.type === 'EXTERNAL' && currentUser?.publisherId) {
      return EXTERNAL_PUBLISHER_NAV_CONTEXT_LIST;
    } else if (currentUser?.type === 'EXTERNAL' && currentUser?.partnerId) {
      return EXTERNAL_PARTNER_NAV_CONTEXT_LIST;
    } else if (currentUser?.type === 'INTERNAL') {
      return INTERNAL_NAV_CONTEXT_LIST;
    }
    return [];
  }

  private markCurrentSelectedNavItem(navItems: CtrlNavigationEntry[], path?: string): CtrlNavigationEntry[] {
    const currentPath = path || this.router.url;
    navItems.forEach(parent => {
      parent.isSelected = false;
      if (parent.children) {
        parent.children.map((child: CtrlNavigationEntry) => {
          child.isSelected = false;
          const childPathString = child.path.join('/');
          if (currentPath.includes(childPathString)) {
            child.isSelected = true;
          }
          return child;
        });
      }
      if (this.isParentSelected(parent, currentPath)) {
        parent.isSelected = true;
      }
    });
    return navItems;
  }

  private isParentSelected(parent: CtrlNavigationEntry, currentPath: string): boolean {
    const hasNoChildren = !parent?.children || parent?.children?.length === 0;
    const currentPathWithoutSpearad = trimStart(currentPath.replace('/spearad', ''), '/');
    const parentFullPath = parent?.path?.join('/');
    return !!(hasNoChildren && parent.path && (currentPathWithoutSpearad === parentFullPath));
  }

}
